import React, { useMemo, useState } from 'react';
import { Helmet } from "react-helmet-async";
import {
    ConfigProvider,
    theme,
    Table,
    Input,
    Avatar,
    Tag,
    Button,
    Tooltip,
    Modal
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    Minus, Plus, CircleChevronDown, Funnel,
    Phone, Clock, MessageSquareDashed, Paperclip, Smile, Send,
    CircleArrowUp, Undo2, RefreshCw, UserRoundPlus,
    CirclePercent, Bot, Check, CircleHelp, Waypoints, PenTool, Mail,
    CircleX, Play, HelpCircle,
    X,
    File,
    ChevronDown
} from 'lucide-react';

import Layout from '../../Layout'; // Giữ nguyên import layout của bạn
import { Link } from 'react-router-dom';
import { LeftOutlined } from "@ant-design/icons";
import { mixformatCurrency } from 'services/mixin';

const { TextArea } = Input;

interface CommentItem {
    id: number,
    author: string,
    text: any,
    time: string,
}

interface HistoryItem {
    status: number;
}

interface ExpenseDetail {
    status: number,
    step: number,
    history: HistoryItem[],
}

// 1. Định nghĩa map trạng thái
const STATUS_MAP: Record<number, { color: string; label: string }> = {
    1: { color: 'warning', label: 'Đang duyệt' },
    2: { color: 'success', label: 'Đã duyệt' },
    3: { color: 'error', label: 'Không duyệt' },
};


// 1. Định nghĩa MAP chứa sẵn Icon Component
const STEP_MAP: Record<number, { color: string; label: string; icon: React.ReactNode }> = {
    1: { color: 'warning', label: 'Phòng nhân sự duyệt', icon: <Play size={10} className="inline-block mr-0.5" /> },
    2: { color: 'success', label: 'Bước duyệt', icon: <Check size={10} className="inline-block mr-0.5" /> },
    3: { color: 'error', label: 'Bước duyệt', icon: <CircleX size={10} className="inline-block mr-0.5" /> },
};

const tabs = [
    { id: 1, label: 'Thảo luận' },
    { id: 2, label: 'Lịch sử hoạt động' },

]


export default function DecisionDetail() {

    const [isDarkMode, setIsDarkMode] = useState(false);
    const [comments, setComments] = useState<CommentItem[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [tab, setTab] = useState(1)
    const [showModalApprove, setShowModalApprove] = useState(false)
    const [showModalDigitalSign, setShowModalDigitalSign] = useState(false)
    const [showModalUndo, setShowModalUndo] = useState(false)

    // ================= DỮ LIỆU MOCK =================
    const [detail, setDetail] = useState<ExpenseDetail>({
        status: 1,
        step: 1,
        history: [
            { status: 1 }
        ],
    });
    const stepInfo = STEP_MAP[detail?.step];



    const handleSendComment = () => {
        if (!inputValue.trim()) return;
        const newComment = { id: Date.now(), author: 'Mr. Huấn', text: inputValue, time: 'Vừa xong' };
        setComments([...comments, newComment]);
        setInputValue('');
    };


    // ====Collapse States====
    const [expandedBlocks, setExpandedBlocks] = useState({
        general: true,
        salary: true,
    });

    const toggleBlock = (key: keyof typeof expandedBlocks) => {
        setExpandedBlocks(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // ================= THEME & UI COMPONENTS =================
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
        },
        components: { Table: { headerBg: isDarkMode ? '#374151' : '#f9fafb' } },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const CardHeader = ({ title, blockKey, extra }: { title: any, blockKey: keyof typeof expandedBlocks, extra?: any }) => (
        <div className="flex justify-between items-center mb-4 pb-2 border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-2 -mx-4 -mt-4 rounded-t-lg">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
            <div className="flex items-center gap-3">
                {extra}
                <div onClick={() => toggleBlock(blockKey)} className="cursor-pointer text-gray-400 hover:text-[#3e91ab] p-1">
                    {expandedBlocks[blockKey] ? <Minus size={16} /> : <Plus size={16} />}
                </div>
            </div>
        </div>
    );

    return (
        <Layout>
            <Helmet>
                <title> Chi tiết quyết định | Khothi</title>
            </Helmet>
            <div className="flex gap-2 mb-4 justify-between items-center">
                <h2 className="text-lg text-gray-900 font-semibold dark:text-white">
                    Chi tiết quyết định
                </h2>
                {/* <button className="flex items-center gap-2 px-3 py-1.5 bg-[#3e91ab] text-white rounded text-sm font-medium hover:bg-[#357f96] transition-colors" onClick={() => navigate(`/employment-contract/add`)}>
                    <Plus size={16} /> Thêm mới
                </button> */}
            </div>
            <ConfigProvider theme={antdThemeConfig}>
                <div className={`min-h-screen pb-10 transition-colors duration-300`}>

                    {/* --- HEADER ACTIONS --- */}
                    <div className="border-b dark:border-gray-700 px-4 md:px-6 py-3 flex flex-wrap justify-between items-center gap-4 sticky top-[64px] bg-white dark:bg-[#1f1f1f] z-10 shadow-sm">
                        <div className="flex sm:items-center gap-4 text-sm sm:flex-row flex-col items-start">
                            <div className="flex sm:items-center gap-4 text-sm items-start">
                                <Link to="/decision" className="flex items-center gap-2">
                                    <LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" />
                                </Link>
                                <span className="font-medium text-[#3e91ab] border-b-2 border-[#3e91ab] pb-1 cursor-pointer">Chi tiết</span>
                            </div>
                        </div>

                        <div className='overflow-x-auto no-scrollbar'>
                            <div className="flex items-center gap-4 xl:gap-5 w-full xl:w-auto ml-auto min-w-max text-000000e0">
                                <Button type="primary" icon={<Bot size={18} />}>
                                    1AI Monitor
                                </Button>
                                <button className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><Check size={14} /> Duyệt</button>
                                <button className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><Undo2 size={14} /> Hoàn duyệt</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><PenTool size={14} /> Ký số</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><File size={14} /> Biểu mẫu <ChevronDown size={14} /></button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><CircleChevronDown size={20} /></button>
                            </div>
                        </div>
                    </div>

                    {/* --- MAIN LAYOUT --- */}
                    <div className="mx-auto mt-4 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">

                        {/* CỘT TRÁI CÓ TABS CHÍNH */}
                        <div className="min-w-0 overflow-hidden">
                            <div className="flex flex-col gap-6 pt-0">

                                {/* 1 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Thông tin chung" blockKey="general" />
                                    {expandedBlocks.general && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8 text-[14px] mt-4">

                                            {/* Cột 1 */}
                                            <div className="flex flex-col gap-5">
                                                <div>
                                                    <p className="text-gray-400 mb-1">Số quyết định</p>
                                                    <p className="font-medium text-gray-800 dark:text-gray-200">10/03/2026</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Ngày hiệu lực</p>
                                                    <p className="text-gray-800 dark:text-gray-200">10/03/2026</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Nhân viên</p>
                                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#fff2e8] text-[#ff7a45]">
                                                        <span className="font-medium">Nguyễn Quốc Trung</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Phòng ban cũ</p>
                                                    <p className="text-gray-800 dark:text-gray-200">[TTP] Công ty TTP › Ban cố vấn</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Vị trí cũ</p>
                                                    <p className="text-gray-800 dark:text-gray-200">(1Office) Cố vấn cao cấp phát triển sản phẩm</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Chức vụ cũ</p>
                                                    <p className="text-gray-800 dark:text-gray-200">(1Office) Chuyên viên</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Hợp đồng cũ</p>
                                                    <p className="text-gray-800 dark:text-gray-200">Hợp đồng 3 năm</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Cấp ra quyết định</p>
                                                    <p className="text-gray-800 dark:text-gray-200">Giám đốc tài chính</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Trạng thái</p>
                                                    <Tag color={STATUS_MAP[detail?.status]?.color || 'blue'}>
                                                        {STATUS_MAP[detail?.status]?.label || 'Đang duyệt'}
                                                    </Tag>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Bước duyệt</p>
                                                    <Tag color={STEP_MAP[detail?.step]?.color || 'warning'}>
                                                        {STEP_MAP[detail?.step]?.label || 'Phòng nhân sự duyệt'}
                                                        {stepInfo?.icon || <HelpCircle size={12} className="inline-block ml-1" />}
                                                    </Tag>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Mô tả</p>
                                                    <p className="text-gray-800 dark:text-gray-200">--</p>
                                                </div>
                                            </div>

                                            {/* Cột 2 */}
                                            <div className="flex flex-col gap-5">
                                                <div>
                                                    <p className="text-gray-400 mb-1">Ngày quyết định</p>
                                                    <p className="text-gray-800 dark:text-gray-200">10/03/2026</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Ngày hết hiệu lực</p>
                                                    <p className="text-gray-800 dark:text-gray-200">10/03/2026</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Lý do bổ nhiệm</p>
                                                    <p className="text-gray-800 dark:text-gray-200">--</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Phòng ban bổ nhiệm</p>
                                                    <p className="text-gray-800 dark:text-gray-200">Phòng kế toán</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Vị trí bổ nhiệm</p>
                                                    <p className="text-gray-800 dark:text-gray-200">Giám đốc thương hiệu</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Chức vụ bổ nhiệm</p>
                                                    <p className="text-gray-800 dark:text-gray-200">(1Office) Phó Giám Đốc</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Hợp đồng mới</p>
                                                    <p className="text-gray-800 dark:text-gray-200">HĐ học việc- Yến</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Người theo dõi</p>
                                                    <p className="text-gray-800 dark:text-gray-200">--</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Người duyệt</p>
                                                    <div className="flex flex-col gap-2">
                                                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#f4f4f4] dark:bg-[#111827] w-fit">
                                                            <Avatar size={20} className="bg-gray-500 text-[10px]" src='https://trial.1office.vn/f/p/TDI0OHljd045SHpkUkF0d2REV0NRQ1pCZlJnU1dQUGZoTm5rT0dRMWVpYmFiREZsL2lDd1dNTFJwbEtUbnJjdVZVWkZ6T2FWU2ZSNkszZVpyeWxxWENWU29kQnBnNEgzWGM5NjVEL0xGNHVUSUNOWDRsaz0/1535570912638031ae4eb2b025045322.25.11.2022.small.png' />
                                                            <span className="text-gray-800 dark:text-gray-200">Tuyendung</span>
                                                        </div>
                                                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#f4f4f4] dark:bg-[#111827] w-fit">
                                                            <Avatar size={20} className="bg-gray-500 text-[10px]" src='https://trial.1office.vn/f/p/TDI0OHljd045SHpkUkF0d2REV0NRQ1pCZlJnU1dQUGZoTm5rT0dRMWVpYmFiREZsL2lDd1dNTFJwbEtUbnJjdVZVWkZ6T2FWU2ZSNkszZVpyeWxxWENWU29kQnBnNEgzWGM5NjVEL0xGNHVUSUNOWDRsaz0/1535570912638031ae4eb2b025045322.25.11.2022.small.png' />
                                                            <span className="text-gray-800 dark:text-gray-200">Lê Tuấn Anh</span>
                                                        </div>
                                                        <span className="text-[#3E91AB] cursor-pointer text-xs font-medium">Xem thêm (+2)</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Điều chỉnh chế độ</p>
                                                    <p className="text-gray-800 dark:text-gray-200">Có</p>
                                                </div>
                                            </div>

                                        </div>
                                    )}
                                </div>

                                {/* 2 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Lương & phụ cấp" blockKey="salary" />
                                    {expandedBlocks.salary && (
                                        <div className="p-4 md:p-6 space-y-6 text-sm">

                                            {/* Lương và phụ cấp cũ */}
                                            <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-0">
                                                {/* Label Bên trái */}
                                                <div className="w-full md:w-1/3 font-semibold text-gray-700 dark:text-gray-300 pt-1">
                                                    Lương và phụ cấp cũ
                                                </div>

                                                {/* Data Bên phải */}
                                                <div className="w-full md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    {/* Item 1 */}
                                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-2">
                                                        <div className="text-xs text-gray-400 dark:text-gray-400 mb-1">
                                                            Lương cơ bản gross
                                                        </div>
                                                        <div className="font-semibold text-gray-800 dark:text-gray-200">
                                                            4,750,000
                                                        </div>
                                                    </div>

                                                    {/* Item 2 */}
                                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-2 flex items-end justify-between sm:justify-start">
                                                        <div>
                                                            <div className="text-xs text-transparent select-none mb-1 hidden sm:block">
                                                                &nbsp;
                                                            </div>
                                                            <div className="font-semibold text-gray-800 dark:text-gray-200">
                                                                500,000/
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Lương và phụ cấp mới */}
                                            <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-0">
                                                {/* Label Bên trái */}
                                                <div className="w-full md:w-1/3 font-semibold text-gray-700 dark:text-gray-300 pt-1">
                                                    Lương và phụ cấp mới
                                                </div>

                                                {/* Data Bên phải */}
                                                <div className="w-full md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    {/* Item 1 */}
                                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-2">
                                                        <div className="text-xs text-gray-400 dark:text-gray-400 mb-1">
                                                            Lương cơ bản gross
                                                        </div>
                                                        <div className="font-semibold text-gray-800 dark:text-gray-200">
                                                            9,000,000
                                                        </div>
                                                    </div>

                                                    {/* Item 2 */}
                                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-2">
                                                        <div className="text-xs text-gray-400 dark:text-gray-400 mb-1">
                                                            Lương khoán công việc
                                                        </div>
                                                        <div className="font-semibold text-gray-800 dark:text-gray-200">
                                                            9,000,000
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>

                        {/* CỘT PHẢI (THẢO LUẬN / LỊCH SỬ) - Giữ nguyên logic cũ */}
                        <div className="min-w-0 flex flex-col gap-4 h-full">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                <div className="flex justify-between items-center mb-4 pb-2 border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-2 -mx-4 -mt-4 rounded-t-lg">
                                    <div className="flex sm:items-center gap-4 text-sm sm:flex-row flex-col items-start">
                                        {tabs.map((item) => (
                                            <button
                                                type="button"
                                                onClick={() => setTab(item.id)}
                                                className={`font-medium pb-1 border-b-2 transition-colors ${item.id === tab
                                                    ? "text-[#3e91ab] border-[#3e91ab]"
                                                    : "text-gray-500 dark:text-gray-400 border-transparent"
                                                    }`}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div onClick={() => toggleBlock('comment')} className="cursor-pointer text-gray-400 hover:text-[#3e91ab] p-1">
                                        {expandedBlocks['comment'] ? <Minus size={16} /> : <Plus size={16} />}
                                    </div>
                                </div>
                                {expandedBlocks.comment && (
                                    <div className="flex flex-col mt-2 relative">

                                        {/* Khu vực hiển thị bình luận */}
                                        <div className="flex-1 overflow-y-auto mb-4 pr-2">
                                            {comments.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center text-gray-400 opacity-60">
                                                    <MessageSquareDashed size={48} className="mb-3" strokeWidth={1} />
                                                    <p className="text-sm">Không có thảo luận nào</p>
                                                </div>
                                                // EMPTY STATE NẾU KHÔNG CÓ BÌNH LUẬN
                                            ) : (
                                                // NẾU CÓ BÌNH LUẬN
                                                comments.map(comment => (
                                                    <div key={comment.id} className="flex gap-3 mb-6">
                                                        <Avatar style={{ backgroundColor: '#84cc16' }}>{comment.author.charAt(0)}</Avatar>
                                                        <div className="flex-1">
                                                            <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">{comment.author}</span>
                                                            <div className="bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg rounded-tl-none mt-1 text-sm text-gray-800 w-fit">
                                                                {comment.text}
                                                            </div>
                                                            <div className="flex items-center gap-4 mt-2 text-[11px] text-gray-500">
                                                                <span>{comment.time}</span>
                                                                <span className="cursor-pointer hover:text-[#3e91ab]">Thích</span>
                                                                <span className="cursor-pointer hover:text-[#3e91ab]">Trả lời</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                        {/* Ô Input nhập liệu */}
                                        <div className="flex justify-between border border-gray-200 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700/30 shrink-0 mt-auto">
                                            <div className="flex items-center gap-2 mb-2">
                                                <CircleArrowUp className="cursor-pointer text-gray-400 hover:text-[#3e91ab]" size={16} />
                                                <Input
                                                    bordered={false}
                                                    placeholder="Viết thảo luận..."
                                                    className="flex-1 text-sm"
                                                    value={inputValue}
                                                    onChange={(e) => setInputValue(e.target.value)}
                                                    onPressEnter={handleSendComment}
                                                />
                                            </div>
                                            <div className="flex gap-3 items-center text-gray-400 pl-8 pr-2">
                                                <Paperclip size={16} className="cursor-pointer hover:text-[#3e91ab]" />
                                                <Smile size={16} className="cursor-pointer hover:text-[#3e91ab]" />
                                                <Send size={16} className="cursor-pointer text-[#3e91ab]" />
                                            </div>
                                        </div>

                                    </div>
                                )}

                            </div>
                        </div>
                    </div>

                </div>
            </ConfigProvider>
        </Layout>
    );
}